/**
 * InvoiceManager - Create, send, and manage customer invoices
 * Integrates with Contacts and Chart of Accounts
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Invoice, InvoiceLine, Contact, Account, TaxRate } from '../../../types/accounting';
import { accountingApi } from '../../../services/accountingApi';
import { todayLocalIso } from '../../../utils/inputFormatters';
import { InvoicePDFService } from '../../../services/invoice-pdf-service';

interface InvoiceManagerProps {
  businessId: number;
  formatCurrency: (amount: number) => string;
}

type StatusFilter = 'all' | 'draft' | 'sent' | 'paid' | 'overdue' | 'void';

interface LineFormData {
  description: string;
  quantity: string;
  unit_price: string;
  account_id: number | null;
  tax_rate_id: number | null;
}

const EMPTY_LINE: LineFormData = {
  description: '',
  quantity: '1',
  unit_price: '0.00',
  account_id: null,
  tax_rate_id: null,
};

const DEFAULT_TERMS = 'Payment due by the due date. Interest of 1.5% per month on late balances.';

/** Days a customer's payment terms allow, so the due date follows the contact instead of a fixed 30 */
const TERM_DAYS: Record<string, number> = {
  due_on_receipt: 0, net_15: 15, net_30: 30, net_45: 45, net_60: 60, net_90: 90,
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: '#f3f4f6', text: '#374151' },
  sent: { bg: '#dbeafe', text: '#1d4ed8' },
  viewed: { bg: '#e0e7ff', text: '#4338ca' },
  paid: { bg: '#d1fae5', text: '#059669' },
  overdue: { bg: '#fee2e2', text: '#dc2626' },
  void: { bg: '#f3f4f6', text: '#9ca3af' },
};

const InvoiceManager: React.FC<InvoiceManagerProps> = ({ businessId, formatCurrency }) => {
  // List state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [contactId, setContactId] = useState<number | null>(null);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [formLines, setFormLines] = useState<LineFormData[]>([{ ...EMPTY_LINE }]);

  // Lookup data
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState<number | null>(null);
  const [paymentAccountId, setPaymentAccountId] = useState<number | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayLocalIso());

  // Load invoices
  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusParam = statusFilter === 'all' ? undefined : statusFilter;
      const response = await accountingApi.getInvoices(businessId, statusParam);
      if (response.status === 'success' && response.data) {
        setInvoices(response.data.invoices);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [businessId, statusFilter]);

  // Load lookup data (contacts, accounts, tax rates)
  const loadLookupData = useCallback(async () => {
    try {
      const [contactsRes, accountsRes, taxRatesRes] = await Promise.all([
        accountingApi.getContacts(businessId, 'customer'),
        accountingApi.getBusinessAccounts(businessId),
        accountingApi.getTaxRates(businessId),
      ]);

      if (contactsRes.status === 'success' && contactsRes.data) {
        setContacts(contactsRes.data.contacts);
      }
      if (accountsRes.status === 'success' && accountsRes.data) {
        // Flatten accounts from grouped object
        const allAccounts: Account[] = [];
        Object.values(accountsRes.data.accounts).forEach((group) => {
          allAccounts.push(...group);
        });
        setAccounts(allAccounts);
      }
      if (taxRatesRes.status === 'success' && taxRatesRes.data) {
        setTaxRates(taxRatesRes.data.tax_rates);
      }
    } catch (err: any) {
      // Silently fail on lookup data - user can still view invoices
    }
  }, [businessId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    loadLookupData();
  }, [loadLookupData]);

  // Auto-clear success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Due date follows the customer's payment terms, falling back to net 30
  useEffect(() => {
    if (!issueDate || editingInvoiceId !== null) return;
    const customer = contacts.find((c) => c.id === contactId);
    const days = TERM_DAYS[customer?.payment_terms || 'net_30'] ?? 30;
    const due = new Date(`${issueDate}T00:00:00`);
    due.setDate(due.getDate() + days);
    setDueDate(due.toISOString().split('T')[0]);
  }, [issueDate, contactId, contacts, editingInvoiceId]);

  // Revenue accounts for line item dropdown
  const revenueAccounts = accounts.filter((a) => a.account_type === 'revenue' && a.is_active);

  // A registrant's invoice should default to charging tax, posted to a revenue account
  const defaultTaxRateId = taxRates.find((tr) => tr.is_default)?.id ?? null;
  const defaultRevenueAccountId =
    revenueAccounts.find((a) => a.account_name.toLowerCase().includes('service revenue'))?.id
    ?? revenueAccounts[0]?.id ?? null;
  const newLine = (): LineFormData => ({
    ...EMPTY_LINE,
    account_id: defaultRevenueAccountId,
    tax_rate_id: defaultTaxRateId,
  });

  // A blank first line was created before the lookups arrived; fill in its defaults once they do
  useEffect(() => {
    if (editingInvoiceId !== null) return;
    setFormLines((current) => current.map((line) =>
      line.account_id === null && line.tax_rate_id === null && !line.description
        ? { ...line, account_id: defaultRevenueAccountId, tax_rate_id: defaultTaxRateId }
        : line
    ));
  }, [defaultRevenueAccountId, defaultTaxRateId, editingInvoiceId]);

  // Cash/bank accounts for payment dropdown
  const cashBankAccounts = accounts.filter(
    (a) =>
      a.is_active &&
      (a.account_type === 'asset') &&
      (a.sub_type === 'cash' || a.sub_type === 'bank' ||
       a.account_name.toLowerCase().includes('cash') ||
       a.account_name.toLowerCase().includes('bank') ||
       a.account_name.toLowerCase().includes('chequing') ||
       a.account_name.toLowerCase().includes('checking') ||
       a.account_name.toLowerCase().includes('savings'))
  );

  // Calculate line totals
  const calculateLineAmount = (line: LineFormData): number => {
    const qty = parseFloat(line.quantity) || 0;
    const price = parseFloat(line.unit_price) || 0;
    return Math.round(qty * price * 100) / 100;
  };

  const calculateLineTax = (line: LineFormData): number => {
    if (!line.tax_rate_id) return 0;
    const taxRate = taxRates.find((tr) => tr.id === line.tax_rate_id);
    if (!taxRate) return 0;
    const amount = calculateLineAmount(line);
    return Math.round(amount * taxRate.rate * 100) / 100;
  };

  const subtotal = formLines.reduce((sum, line) => sum + calculateLineAmount(line), 0);
  const totalTax = formLines.reduce((sum, line) => sum + calculateLineTax(line), 0);
  const grandTotal = subtotal + totalTax;

  // Line item handlers
  const addLine = () => {
    setFormLines([...formLines, newLine()]);
  };

  const removeLine = (index: number) => {
    if (formLines.length <= 1) return;
    setFormLines(formLines.filter((_, i) => i !== index));
  };

  const updateLine = (index: number, field: keyof LineFormData, value: string | number | null) => {
    const updated = [...formLines];
    (updated[index] as any)[field] = value;
    setFormLines(updated);
  };

  // Reset form
  const resetForm = () => {
    setContactId(null);
    setIssueDate(new Date().toISOString().split('T')[0]);
    setDueDate('');
    setNotes('');
    setTerms(DEFAULT_TERMS);
    setFormLines([newLine()]);
    setEditingInvoiceId(null);
    setShowForm(false);
  };

  // Load invoice for editing
  const startEdit = async (invoiceId: number) => {
    try {
      const response = await accountingApi.getInvoice(invoiceId);
      if (response.status === 'success' && response.data) {
        const inv = response.data.invoice;
        setEditingInvoiceId(inv.id);
        setContactId(inv.contact_id);
        setIssueDate(inv.issue_date);
        setDueDate(inv.due_date);
        setNotes(inv.notes || '');
        setTerms(inv.terms || '');
        setFormLines(
          inv.lines.map((line) => ({
            description: line.description,
            quantity: String(line.quantity),
            unit_price: String(line.unit_price),
            account_id: line.account_id,
            tax_rate_id: line.tax_rate_id,
          }))
        );
        setShowForm(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load invoice');
    }
  };

  // Save invoice (create or update)
  const handleSave = async () => {
    if (!contactId) {
      setError('Please select a customer');
      return;
    }
    if (!issueDate || !dueDate) {
      setError('Issue date and due date are required');
      return;
    }
    const validLines = formLines.filter((l) => l.description.trim() && parseFloat(l.unit_price) > 0);
    if (validLines.length === 0) {
      setError('At least one line item with a description and price is required');
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      contact_id: contactId,
      issue_date: issueDate,
      due_date: dueDate,
      notes: notes,
      terms: terms,
      lines: validLines.map((line) => ({
        description: line.description,
        quantity: parseFloat(line.quantity) || 1,
        unit_price: parseFloat(line.unit_price) || 0,
        account_id: line.account_id,
        tax_rate_id: line.tax_rate_id,
      })),
    };

    try {
      if (editingInvoiceId) {
        await accountingApi.updateInvoice(editingInvoiceId, payload);
        setSuccessMessage('Invoice updated');
      } else {
        await accountingApi.createInvoice(businessId, payload);
        setSuccessMessage('Invoice created');
      }
      resetForm();
      loadInvoices();
    } catch (err: any) {
      setError(err.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  // Actions
  const handleSend = async (invoiceId: number) => {
    try {
      await accountingApi.sendInvoice(invoiceId);
      setSuccessMessage('Invoice marked as sent');
      loadInvoices();
    } catch (err: any) {
      setError(err.message || 'Failed to send invoice');
    }
  };

  const handleVoid = async (invoiceId: number) => {
    if (!window.confirm('Are you sure you want to void this invoice?')) return;
    try {
      await accountingApi.voidInvoice(invoiceId);
      setSuccessMessage('Invoice voided');
      loadInvoices();
    } catch (err: any) {
      setError(err.message || 'Failed to void invoice');
    }
  };

  const handleDelete = async (invoiceId: number) => {
    if (!window.confirm('Are you sure you want to delete this draft invoice?')) return;
    try {
      await accountingApi.deleteInvoice(invoiceId);
      setSuccessMessage('Invoice deleted');
      loadInvoices();
    } catch (err: any) {
      setError(err.message || 'Failed to delete invoice');
    }
  };

  const paymentInvoice = invoices.find((i) => i.id === paymentInvoiceId) || null;
  const balanceDue = paymentInvoice
    ? (paymentInvoice.balance_due ?? paymentInvoice.total - (paymentInvoice.amount_paid || 0))
    : 0;

  const openPaymentModal = (invoiceId: number) => {
    const invoice = invoices.find((i) => i.id === invoiceId);
    const owing = invoice ? (invoice.balance_due ?? invoice.total - (invoice.amount_paid || 0)) : 0;
    setPaymentInvoiceId(invoiceId);
    setPaymentAccountId(cashBankAccounts.length > 0 ? cashBankAccounts[0].id : null);
    setPaymentAmount(owing.toFixed(2));
    setPaymentDate(todayLocalIso());
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentInvoiceId || !paymentAccountId) {
      setError('Please select a payment account');
      return;
    }
    try {
      const amount = parseFloat(paymentAmount);
      if (!amount || amount <= 0) {
        setError('Enter a payment amount greater than zero');
        return;
      }
      if (amount > balanceDue + 0.005) {
        setError(`That is more than the ${formatCurrency(balanceDue)} still owing on this invoice`);
        return;
      }
      const response = await accountingApi.recordPayment(paymentInvoiceId, paymentAccountId, amount, paymentDate);
      const remaining = response.data?.invoice?.balance_due ?? 0;
      setSuccessMessage(remaining > 0.005
        ? `Partial payment recorded. ${formatCurrency(remaining)} still owing.`
        : 'Payment recorded and journal entry created');
      setShowPaymentModal(false);
      setPaymentInvoiceId(null);
      loadInvoices();
    } catch (err: any) {
      setError(err.message || 'Failed to record payment');
    }
  };

  const handleDownloadPdf = async (invoice: Invoice) => {
    try {
      // Find the business info from accounts (we need it for PDF header)
      const businessRes = await accountingApi.getBusiness(businessId);
      const business = businessRes.data?.business;

      const pdfService = new InvoicePDFService();
      pdfService.generateInvoicePDF(invoice, business || null, formatCurrency);
    } catch (err: any) {
      setError(err.message || 'Failed to generate PDF');
    }
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    const colors = STATUS_COLORS[status] || STATUS_COLORS.draft;
    return (
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 600,
          backgroundColor: colors.bg,
          color: colors.text,
          textTransform: 'uppercase',
        }}
      >
        {status}
      </span>
    );
  };

  // Filter tabs
  const filterTabs: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'draft', label: 'Draft' },
    { key: 'sent', label: 'Sent' },
    { key: 'paid', label: 'Paid' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'void', label: 'Void' },
  ];

  // ========== RENDER ==========

  return (
    <section className="dashboard-section">
      <h3 style={{ margin: '0 0 0.5rem 0' }}>Invoices</h3>
      <p style={{ color: '#6b7280', margin: '0 0 1rem 0', fontSize: '0.875rem' }}>
        Create and manage customer invoices
      </p>

      {/* Messages */}
      {error && (
        <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '6px', marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 'bold' }}>x</button>
        </div>
      )}
      {successMessage && (
        <div style={{ padding: '0.75rem 1rem', backgroundColor: '#d1fae5', color: '#059669', borderRadius: '6px', marginBottom: '1rem' }}>
          {successMessage}
        </div>
      )}

      {/* Top bar: filter tabs + create button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              style={{
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                border: '1px solid ' + (statusFilter === tab.key ? '#059669' : '#d1d5db'),
                backgroundColor: statusFilter === tab.key ? '#059669' : 'white',
                color: statusFilter === tab.key ? 'white' : '#374151',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: statusFilter === tab.key ? 600 : 400,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#059669',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          + New Invoice
        </button>
      </div>

      {/* Invoice Form */}
      {showForm && (
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>
            {editingInvoiceId ? 'Edit Invoice' : 'New Invoice'}
          </h4>

          {/* Header fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem', fontSize: '0.85rem' }}>Customer *</label>
              <select
                value={contactId || ''}
                onChange={(e) => setContactId(e.target.value ? Number(e.target.value) : null)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
              >
                <option value="">Select customer...</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.display_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem', fontSize: '0.85rem' }}>Issue Date *</label>
              <input
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem', fontSize: '0.85rem' }}>Due Date *</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
              />
            </div>
          </div>

          {/* Line Items */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.85rem' }}>Line Items</label>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Description</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: '80px' }}>Qty</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: '110px' }}>Unit Price</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', width: '160px' }}>Account</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', width: '120px' }}>Tax</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: '100px' }}>Amount</th>
                  <th style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {formLines.map((line, i) => {
                  const lineAmount = calculateLineAmount(line);
                  const lineTax = calculateLineTax(line);
                  return (
                    <tr key={i}>
                      <td style={{ padding: '0.35rem' }}>
                        <input
                          type="text"
                          value={line.description}
                          onChange={(e) => updateLine(i, 'description', e.target.value)}
                          placeholder="Description"
                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        />
                      </td>
                      <td style={{ padding: '0.35rem' }}>
                        <input
                          type="number"
                          value={line.quantity}
                          onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                          min="0"
                          step="0.01"
                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'right' }}
                        />
                      </td>
                      <td style={{ padding: '0.35rem' }}>
                        <input
                          type="number"
                          value={line.unit_price}
                          onChange={(e) => updateLine(i, 'unit_price', e.target.value)}
                          min="0"
                          step="0.01"
                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'right' }}
                        />
                      </td>
                      <td style={{ padding: '0.35rem' }}>
                        <select
                          value={line.account_id || ''}
                          onChange={(e) => updateLine(i, 'account_id', e.target.value ? Number(e.target.value) : null)}
                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}
                        >
                          <option value="">None</option>
                          {revenueAccounts.map((a) => (
                            <option key={a.id} value={a.id}>{a.account_number} - {a.account_name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '0.35rem' }}>
                        <select
                          value={line.tax_rate_id || ''}
                          onChange={(e) => updateLine(i, 'tax_rate_id', e.target.value ? Number(e.target.value) : null)}
                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}
                        >
                          <option value="">No Tax</option>
                          {taxRates.map((tr) => (
                            <option key={tr.id} value={tr.id}>{tr.name} ({tr.rate_percent}%)</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '0.35rem', textAlign: 'right', fontWeight: 500 }}>
                        {formatCurrency(lineAmount)}
                        {lineTax > 0 && (
                          <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                            +{formatCurrency(lineTax)} tax
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.35rem', textAlign: 'center' }}>
                        {formLines.length > 1 && (
                          <button
                            onClick={() => removeLine(i)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1.1rem' }}
                            title="Remove line"
                          >
                            x
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <button
              onClick={addLine}
              style={{
                marginTop: '0.5rem',
                padding: '0.35rem 0.75rem',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              + Add Line
            </button>
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
            <div style={{ width: '250px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', fontSize: '0.9rem' }}>
                <span>Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              {totalTax > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', fontSize: '0.9rem' }}>
                  <span>Tax:</span>
                  <span>{formatCurrency(totalTax)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '1.05rem', fontWeight: 700, borderTop: '2px solid #374151' }}>
                <span>Total:</span>
                <span>{formatCurrency(grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* Notes and Terms */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem', fontSize: '0.85rem' }}>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes to customer..."
                rows={3}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', resize: 'vertical' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem', fontSize: '0.85rem' }}>Terms</label>
              <textarea
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                placeholder="Payment terms..."
                rows={3}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Form buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              onClick={resetForm}
              style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving...' : editingInvoiceId ? 'Update Invoice' : 'Create Invoice'}
            </button>
          </div>
        </div>
      )}

      {/* Invoice List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading invoices...</div>
      ) : invoices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          {statusFilter === 'all'
            ? 'No invoices yet. Click "+ New Invoice" to create one.'
            : 'No ' + statusFilter + ' invoices found.'}
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflowX: 'auto' }}>
          <table className="invoice-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Invoice #</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Customer</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Due</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Total</th>
                <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.75rem', fontWeight: 500 }}>{inv.invoice_number}</td>
                  <td style={{ padding: '0.75rem' }}>{inv.contact_display_name}</td>
                  <td style={{ padding: '0.75rem' }}>{inv.issue_date}</td>
                  <td style={{ padding: '0.75rem' }}>{inv.due_date}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 500 }}>
                    {formatCurrency(inv.total)}
                    {(inv.amount_paid || 0) > 0 && inv.status !== 'paid' && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>
                        {formatCurrency(inv.balance_due ?? inv.total - (inv.amount_paid || 0))} owing
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <StatusBadge status={inv.status} />
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {/* Download PDF */}
                      <button
                        onClick={() => handleDownloadPdf(inv)}
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                        title="Download PDF"
                      >
                        PDF
                      </button>

                      {inv.status === 'draft' && (
                        <>
                          <button
                            onClick={() => startEdit(inv.id)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '4px', cursor: 'pointer', color: '#1d4ed8' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleSend(inv.id)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '4px', cursor: 'pointer', color: '#059669' }}
                          >
                            Send
                          </button>
                          <button
                            onClick={() => handleDelete(inv.id)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer', color: '#dc2626' }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {(inv.status === 'sent' || inv.status === 'viewed' || inv.status === 'overdue') && (
                        <>
                          <button
                            onClick={() => openPaymentModal(inv.id)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '4px', cursor: 'pointer', color: '#059669' }}
                          >
                            Record Payment
                          </button>
                          {(inv.amount_paid || 0) === 0 && (
                          <button
                            onClick={() => handleVoid(inv.id)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', color: '#6b7280' }}
                          >
                            Void
                          </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ margin: '0 0 1rem 0' }}>Record Payment</h4>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {paymentInvoice
                ? `${paymentInvoice.invoice_number} has ${formatCurrency(balanceDue)} still owing. Record the whole amount or part of it; a journal entry is created either way.`
                : 'A journal entry is created automatically.'}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem', fontSize: '0.85rem' }}>
                  Amount received *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={balanceDue}
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem', fontSize: '0.85rem' }}>
                  Date received *
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.25rem', fontSize: '0.85rem' }}>
                Payment Account *
              </label>
              <select
                value={paymentAccountId || ''}
                onChange={(e) => setPaymentAccountId(e.target.value ? Number(e.target.value) : null)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
              >
                <option value="">Select account...</option>
                {cashBankAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.account_number} - {a.account_name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPaymentModal(false)}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={!paymentAccountId || !paymentAmount || !paymentDate}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: paymentAccountId && paymentAmount && paymentDate ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                  opacity: paymentAccountId && paymentAmount && paymentDate ? 1 : 0.5,
                }}
              >
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default InvoiceManager;
